/**
 * 소셜 로그인/가입 — Google · Apple 2종.
 *
 * BE: `POST /api/v1/user/auth/social/{google,apple}` — **이미 연결된 계정이면 이 한 번으로 끝난다.**
 * 처음 보는 계정이면 회원을 만들지 않고 `NEED_AGE` + 가입 대기표가 오고, 연령을 확인해
 * `POST /auth/social/signup` 으로 되가져와야 가입이 끝난다(OI-43).
 *
 * 2026-08-04 재설계: 본인인증(NICE)·이메일 로그인에 이어 약관 동의·닉네임 입력 화면까지 없앴다.
 * 약관은 로그인 화면의 안내 문구·링크로 갈음하고, 닉네임은 provider 이름(없으면 서버가 자동 생성)을 쓴다.
 */
import { apiPost } from "./client";
import type { TokenResponse } from "./auth";
import i18n from "../i18n";

/**
 * 소셜 인증 응답 — **두 갈래다** (OI-43 · 2026-09-15).
 *
 * - `LOGGED_IN` — 이미 연결된 계정이라 토큰이 나왔다. `newMember` 는 이번 호출에서 회원이
 *   만들어졌는지(분석 이벤트 구분용) — `completeSocialSignup` 의 응답에서만 true 다.
 * - `NEED_AGE` — **처음 보는 계정**이라 서버가 아직 회원을 만들지 않았다. 연령을 확인해
 *   `signupTicket` 과 함께 {@link completeSocialSignup} 을 불러야 가입이 끝난다.
 *
 * 연령을 소셜 인증 **뒤에** 묻는 이유가 이 갈래다 — 가입인지 로그인인지는 인증을 마쳐야
 * 알 수 있는데, 연령은 가입에만 물어야 한다. 종전에는 인증 전에 물어서 이미 가입한 사람까지
 * 매번 생년월일을 요구받았다.
 */
export type SocialAuthResult =
  | { kind: "LOGGED_IN"; tokens: TokenResponse; newMember: boolean; signupTicket?: null }
  | { kind: "NEED_AGE"; tokens?: null; newMember?: boolean; signupTicket: string };

/**
 * Google `start` 요청 — 두 가지 형태 중 하나:
 *  - 네이티브 (GIS): `{ idToken }`
 *  - 웹 Authorization Code Flow: `{ code, redirectUri }`
 */
export type GoogleIdTokenStart = { idToken: string };
export type GoogleCodeStart = { code: string; redirectUri: string };
export type GoogleStartReq = GoogleIdTokenStart | GoogleCodeStart;

export async function startGoogleAuth(req: GoogleStartReq): Promise<SocialAuthResult> {
  return apiPost<SocialAuthResult>("/auth/social/google", req);
}

/**
 * Apple `start` 요청 — 두 가지 형태 중 하나:
 *  - iOS 네이티브: `{ idToken, name? }` (ASAuthorizationController identityToken)
 *  - 웹/Android Authorization Code Flow: `{ code, redirectUri, name? }`
 *
 * Apple id_token 에는 이름이 없어, 최초 인증 시 받은 이름을 `name` 으로 함께 전달한다
 * (재로그인 시엔 보통 undefined).
 */
export type AppleIdTokenStart = { idToken: string; name?: string };
export type AppleCodeStart = { code: string; redirectUri: string; name?: string };
export type AppleStartReq = AppleIdTokenStart | AppleCodeStart;

export async function startAppleAuth(req: AppleStartReq): Promise<SocialAuthResult> {
  return apiPost<SocialAuthResult>("/auth/social/apple", req);
}

/**
 * 가입 마무리 — `NEED_AGE` 로 받은 대기표 + 확인한 생년월일 (OI-43).
 *
 * 대기표는 **1회용**이라 실패하면 소셜 로그인부터 다시 해야 한다(서버가 GETDEL 로 소비한다).
 * 소셜 인증을 다시 시키지 않는 이유는 웹·안드로이드의 authorization code 가 1회용이라
 * 같은 code 로는 재시도가 성립하지 않기 때문이다.
 */
export async function completeSocialSignup(req: {
  signupTicket: string;
  birthYmd: string;
}): Promise<SocialAuthResult> {
  return apiPost<SocialAuthResult>("/auth/social/signup", req);
}

/**
 * 서버가 토큰 없이 응답한 경우 — 실질적으로 BE/FE 버전 불일치다.
 * (2026-08-04 이전 BE 는 신규 계정에 `NEED_SIGNUP` + tokens:null 을 돌려줬다.)
 * 가드가 없으면 "Cannot read properties of null" 로만 보여 원인 파악이 어렵다.
 */
export function assertTokens(result: SocialAuthResult): TokenResponse {
  if (!result?.tokens) {
    throw new Error(
      i18n.t("auth.noTokenFromServer"),
    );
  }
  return result.tokens;
}
